import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { DestinationActions } from './actions';
import { ApplicationState, DestinationSelectors } from '../reducers';
import { Destination } from '../../objects/destination';
import { BaseActionService } from './base.action.service';
import { Storefront } from './storefront.service';

/**
 * DestinationActionService abstracts communication with Store
 */
export interface IDestinationActionService {
  setDestination(destination: Destination | null): void;
  loadDestination(slugOrId: number | string): void;
  loadDestinations(storefront: Storefront | null): void;
  getDestination(): Observable<Destination | null>;
  getDestinations(): Observable<Destination[] | null>;
  getDestinationLoading(): Observable<boolean>;
  getDestinationsLoading(): Observable<boolean>;
  getDestinationError(): Observable<Error | null>;
}

@Injectable({
  providedIn: 'root'
})
export class DestinationActionService
  extends BaseActionService<ApplicationState>
  implements IDestinationActionService {
  protected store: Store<ApplicationState>;


  constructor() {
    const store = inject<Store<ApplicationState>>(Store);

    super(store);

    this.store = store;
  }

  readonly destination = this.selectSignal(DestinationSelectors.getDestination);
  readonly destinations = this.selectSignal(DestinationSelectors.getDestinations);
  readonly destinationLoading = this.selectSignal(DestinationSelectors.getDestinationLoading);
  readonly destinationsLoading = this.selectSignal(DestinationSelectors.getDestinationsLoading);
  readonly destinationError = this.selectSignal(DestinationSelectors.getDestinationError);

  setDestination(destination: Destination | null) {
    super.load(DestinationActions.setDestination({ destination }));
  }

  loadDestination(slugOrId: number | string): void {
    super.load(DestinationActions.loadDestination({ slugOrId }));
  }

  loadDestinations(storefront: Storefront | null): void {
    super.load(DestinationActions.loadDestinations({ storefront }));
  }

  getDestination(): Observable<Destination | null> {
    return super.get(DestinationSelectors.getDestination);
  }

  getDestinations(): Observable<Destination[] | null> {
    return super.get(DestinationSelectors.getDestinations);
  }

  getDestinationLoading(): Observable<boolean> {
    return super.get(DestinationSelectors.getDestinationLoading);
  }

  getDestinationsLoading(): Observable<boolean> {
    return super.get(DestinationSelectors.getDestinationsLoading);
  }

  getDestinationError(): Observable<Error | null> {
    return super.get(DestinationSelectors.getDestinationError);
  }
}
